import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Checkbox, mergeStyles, composeRenderFunction, DetailsListLayoutMode, DetailsHeader } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withSegmentation } from './segmentation/with-segmentation';
import { withSubColumns } from './sub-columns/with-sub-columns';
import { CampaignDetailsRow } from './campaign-details-row';
import { Pagination } from './pagination';
import { processColumns } from './utils/process-columns';
import gridStyle from './style/grid';
import { ParentColumnPlacement } from './utils/constants';

class BaseCampaignDetailsList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      inlineEditPanel: null,
    };
    this.containerRef = React.createRef();
    this.scrollBarRef = React.createRef();
    this.scrollBarFixedRef = React.createRef();
    this.detailsListRef = React.createRef();
    this.intersectionObserver = null;
  }

  componentDidMount() {
    if (!this.props.disableFixScrollbar) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        const ratio = entries[entries.length - 1].intersectionRatio;
        const scrollBar = this.scrollBarRef.current;
        const scrollBarFixed = this.scrollBarFixedRef.current;
        if (!scrollBar || !scrollBarFixed) {
          return;
        }
        if (ratio > 0) {
          scrollBar.classList.remove('invisible');
          scrollBarFixed.classList.add('invisible');
          this.resetScrollBarPosition();
        } else {
          scrollBar.classList.add('invisible');
          scrollBarFixed.classList.remove('invisible');
        }
      });
      if (this.scrollBarRef.current) {
        this.intersectionObserver.observe(this.scrollBarRef.current);
      }
    } else {
      this.scrollBarFixedRef.current.classList.add('invisible');
    }

    this.resetScrollBarPosition();
    this.calcContentWidth();
    this.setHeaderFreezingStyle();
    this.props.didMount();
    this.listenViewportResize();
    if (this.props.isGridScrollable) {
      this.addHorizontalScrollEventListener();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sortedColumn !== this.props.sortedColumn || prevProps.selectionMode !== this.props.selectionMode) {
      this.setHeaderFreezingStyle();
    }
    if (prevProps.columns !== this.props.columns) {
      this.setHeaderFreezingStyle();
      this.handleColumnResize();
    }
    if (prevProps.items !== this.props.items) {
      this.resetContentScrollBarPosision();
    }
    this.props.didUpdate(prevProps, this.props);
  }

  componentWillUnmount() {
    if (!this.props.disableFixScrollbar) {
      this.intersectionObserver.unobserve(this.scrollBarRef.current);
    }
    if (this.props.isGridScrollable) {
      this.removeHorizontalScrollEventListener();
    }
  }

  setHeaderFreezingStyle() {
    if (this.props.columns.length === 0) {
      return;
    }
    const { current } = this.containerRef;
    const {
      selectionMode, freezingColumnCount, freezingColumnRTLCount, columns,
    } = this.props;
    const colsCount = freezingColumnCount ? freezingColumnCount + (selectionMode ? 1 : 0) : 0;
    const totalColsCount = columns.length + (selectionMode ? 1 : 0);
    let sumWidth = 0;
    for (let i = 0; i < colsCount; i += 1) {
      const el = current.querySelectorAll('.ms-DetailsHeader .ms-DetailsHeader-cell')[i];
      el.classList.add('freeze-column-cell');
      el.style.left = `${sumWidth}px`;
      sumWidth += el.offsetWidth;
      if (i === colsCount - 1) {
        el.classList.add('has-shadow');
      }
    }
    sumWidth = 0;
    for (let i = 0; i < freezingColumnRTLCount; i += 1) {
      const el = current.querySelectorAll('.ms-DetailsHeader .ms-DetailsHeader-cell')[totalColsCount - i - 1];
      el.classList.add('freeze-column-cell');
      el.style.right = `${sumWidth}px`;
      sumWidth += el.offsetWidth;
      if (i === freezingColumnRTLCount - 1) {
        el.classList.add('has-left-shadow');
      }
    }
    this.resetScrollBarPosition();
  }

  listenViewportResize() {
    if (this.detailsListRef.current) {
      const fluentDetailsList = this.detailsListRef.current;
      const originalUpdate = fluentDetailsList.componentDidUpdate;
      fluentDetailsList.componentDidUpdate = (prevProps, prevState) => {
        originalUpdate.call(fluentDetailsList, prevProps, prevState);
        if (_.get(prevProps, ['viewport', 'width']) !== _.get(fluentDetailsList, ['props', 'viewport', 'width'])) {
          this.handleColumnResize();
        }
      };
    }
  }

  calcContentWidth() {
    if (this.props.columns.length === 0) {
      return;
    }
    const container = this.containerRef.current;
    const header = container.querySelector('.ms-DetailsHeader');
    const width = header.scrollWidth;
    const scrollBar = this.scrollBarRef.current;
    const scrollBarFixed = this.scrollBarFixedRef.current;
    const currentScrollLeft = scrollBar.scrollLeft;
    const offset = parseInt(scrollBar.style.marginLeft || 0, 10) + parseInt(scrollBar.style.marginRight || 0, 10);
    scrollBar.querySelector('.scroll-bar-child').style.width = `${width - offset}px`;
    scrollBarFixed.querySelector('.scroll-bar-child').style.width = `${width - offset}px`;
    header.scrollTo({ left: currentScrollLeft });
  }

  resetScrollBarPosition() {
    if (this.props.columns.length === 0) {
      return;
    }
    const container = this.containerRef.current;
    const scrollBar = this.scrollBarRef.current;
    const scrollBarFixed = this.scrollBarFixedRef.current;
    const headerRow = container.querySelector('.ms-DetailsHeader');
    const headerRowRect = headerRow.getBoundingClientRect();

    const {
      selectionMode, freezingColumnCount, freezingColumnRTLCount, columns,
    } = this.props;
    if (!freezingColumnCount && !freezingColumnRTLCount) {
      const rect = scrollBar.getBoundingClientRect();
      scrollBarFixed.style.left = `${rect.left}px`;
      scrollBarFixed.style.width = `${rect.width}px`;
      return;
    }
    const colsCount = freezingColumnCount + (selectionMode ? 1 : 0);
    const colsCountFromRight = freezingColumnRTLCount - (selectionMode ? 1 : 0);
    const lastFrozenCell = container.querySelectorAll('.ms-DetailsHeader-cell')[colsCount - 1];
    const lastFrozenCellFromRight = container.querySelectorAll('.ms-DetailsHeader-cell')[columns.length - colsCountFromRight];

    const lastFrozenCellRect = lastFrozenCell ? lastFrozenCell.getBoundingClientRect() : { right: headerRowRect.left };
    const lastFrozenCellFromRightRect = lastFrozenCellFromRight ? lastFrozenCellFromRight.getBoundingClientRect() : { left: headerRowRect.right };

    const scrollBarLeft = lastFrozenCellRect.right - headerRowRect.left;
    const scrollBarRight = headerRowRect.right - lastFrozenCellFromRightRect.left;

    scrollBar.style.marginLeft = freezingColumnCount ? `${scrollBarLeft}px` : 0;
    scrollBar.style.marginRight = `${scrollBarRight}px`;
    const rect = scrollBar.getBoundingClientRect();
    scrollBarFixed.style.left = `${rect.left}px`;
    scrollBarFixed.style.width = `${rect.width}px`;
  }

  handleColumnResize = () => {
    this.resetScrollBarPosition();
    this.calcContentWidth();
  };

  dismissInlineEditPanel = () => {
    this.setState({
      inlineEditPanel: null,
    });
  };

  handleSort = (e, column) => {
    const { enableShimmer } = this.props;

    // still loading data
    if (enableShimmer) {
      return;
    }

    const sortedColumn = {
      ...column,
      isSorted: true,
      isSortedDescending: column.isSortedDescending === undefined ? false : !column.isSortedDescending,
    };
    this.props.onSort(sortedColumn);
  };

  addHorizontalScrollEventListener() {
    const { current } = this.containerRef;
    const headerScrollContainer = current.querySelector('.ms-DetailsHeader');
    const contentScrollContainer = current.querySelector('.ms-DetailsList-contentWrapper');
    contentScrollContainer.addEventListener('scroll', this.handleScroll);
    headerScrollContainer.addEventListener('scroll', this.handleScroll);
  }

  removeHorizontalScrollEventListener() {
    const { current } = this.containerRef;
    const headerScrollContainer = current.querySelector('.ms-DetailsHeader');
    const contentScrollContainer = current.querySelector('.ms-DetailsList-contentWrapper');
    contentScrollContainer.removeEventListener('scroll', this.handleScroll);
    headerScrollContainer.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = (e) => {
    if (e.target.classList.contains('invisible')) {
      return;
    }
    const { current } = this.containerRef;
    const headerScrollContainer = current.querySelector('.ms-DetailsHeader');
    const contentScrollContainer = current.querySelector('.ms-DetailsList-contentWrapper');
    const scrollBar = this.scrollBarRef.current;
    const scrollBarFixed = this.scrollBarFixedRef.current;
    const { scrollLeft } = e.target;
    headerScrollContainer.scrollTo({ left: scrollLeft });
    contentScrollContainer.scrollTo({ left: scrollLeft });
    if (e.target === scrollBar || this.props.isGridScrollable) {
      scrollBarFixed.scrollTo({ left: scrollLeft });
    }
    if (e.target === scrollBarFixed || this.props.isGridScrollable) {
      scrollBar.scrollTo({ left: scrollLeft });
    }
  };

  resetContentScrollBarPosision() {
    const { current } = this.containerRef;
    const headerScrollContainer = current.querySelector('.ms-DetailsHeader');
    const contentScrollContainer = current.querySelector('.ms-DetailsList-contentWrapper');
    const { scrollLeft } = headerScrollContainer;
    const scrollBar = this.scrollBarRef.current;
    const scrollBarFixed = this.scrollBarFixedRef.current;
    contentScrollContainer.scrollTo({ left: scrollLeft });
    scrollBar.scrollTo({ left: scrollLeft });
    scrollBarFixed.scrollTo({ left: scrollLeft });
  }

  renderInlineEditPanel = (template) => {
    this.setState({
      inlineEditPanel: template,
    });
  };

  renderHeaderCheckbox = (checked) => {
    const selectedCount = this.props.selection && this.props.selection.count;
    const isIndeterminate = !checked && selectedCount > 0;
    return (
      <Checkbox
        ariaLabel="Check"
        checked={checked}
        indeterminate={isIndeterminate}
        disabled
        inputProps={{ 'aria-hidden': true }}
      />
    );
  };

  renderHeader = (props) => {
    const { toolbar, subColumnProps, headerContainerRef } = this.props;
    return (
      <div className="campaign-details-list-header" ref={headerContainerRef}>
        {toolbar}
        <DetailsHeader
          {...props}
          onRenderDetailsCheckbox={checkBoxProps => this.renderHeaderCheckbox(checkBoxProps.checked)}
          styles={subColumnProps.showSubColumns && { root: { minHeight: 85 }, cellSizer: { height: '100%' } }}
        />
      </div>
    );
  };

  renderRow = (props) => {
    const {
      onRenderRow, selectionMode, freezingColumnCount, freezingColumnRTLCount,
      enableSegmentation, onRenderRowWithSegmentation,
    } = this.props;
    if (enableSegmentation) {
      return onRenderRowWithSegmentation({ ...props, freezingColumnCount, selectionMode });
    }

    const defaultRender = renderProps => (<CampaignDetailsRow {...renderProps} freezingColumnCount={freezingColumnCount} freezingColumnRTLCount={freezingColumnRTLCount} selectionMode={selectionMode} />);

    const finalRender = onRenderRow ? composeRenderFunction(onRenderRow, defaultRender) : defaultRender;

    return finalRender(props);
  };

  renderCheckbox = (props) => {
    if (this.props.onRenderCheckbox) {
      return this.props.onRenderCheckbox(props);
    }
    return (
      <Checkbox ariaLabel="Check" checked={props.checked} disabled />
    );
  };


  render() {
    const {
      props, state, handleSort, handleColumnResize, renderInlineEditPanel, dismissInlineEditPanel,
    } = this;
    const {
      Renderer, placeholder, items, enableShimmer, pager, stickyHeader, stickyTop, freezingColumnCount, sortedColumn, classes,
      onColumnResize = _.noop, isSortedDescendingByDefault, columnProcessors,
    } = props;
    const { inlineEditPanel } = state;

    const columns = processColumns({
      columns: props.columns, sortedColumn, handleSort, renderInlineEditPanel, dismissInlineEditPanel, isSortedDescendingByDefault, columnProcessors, headerContainerRef: props.headerContainerRef,
    });

    const finalOnColumnResize = (...params) => {
      onColumnResize(...params);
      handleColumnResize(...params);
    };

    const fixHeaderClass = mergeStyles({
      '.ms-DetailsList-headerWrapper': {
        position: 'sticky',
        top: stickyTop || 0,
        background: '#fff',
        zIndex: 10,
      },
    });

    return (
      <div className="campaign-details-list-container" ref={this.containerRef}>
        <Renderer
          className={`${classes.CampaignDetailsList}${stickyHeader ? ` ${fixHeaderClass}` : ''}${freezingColumnCount > 0 ? ' has-freeze-column' : ''}`}
          componentRef={this.detailsListRef}
          constrainMode={0}
          layoutMode={DetailsListLayoutMode.justified}
          selectionPreservedOnEmptyClick
          enterModalSelectionOnTouch
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          selectionZoneProps={{
            disableAutoSelectOnInputElements: true,
          }}
          checkButtonAriaLabel="select row"
          {...props}
          columns={columns}
          onRenderDetailsHeader={this.renderHeader}
          onRenderRow={this.renderRow}
          onRenderCheckbox={this.renderCheckbox}
          // disable virtualize when total count less than 200 to increase performance
          onShouldVirtualize={() => items.length > 210}
          onColumnResize={finalOnColumnResize}
          onColumnAdjusted={this.handleColumnResize}
        />
        <div className={classes.scrollBarFixed} onScroll={e => this.handleScroll(e)} ref={this.scrollBarFixedRef}>
          <div className="scroll-bar-child" />
        </div>
        <div className={classes.scrollBar} onScroll={e => this.handleScroll(e)} ref={this.scrollBarRef}>
          <div className="scroll-bar-child" />
        </div>
        {!enableShimmer && placeholder && items.length === 0 && <div className={classes.placeholder}>{placeholder}</div>}
        {pager && items.length > 0 && <Pagination {...pager} />}
        {inlineEditPanel}
      </div>
    );
  }
}

BaseCampaignDetailsList.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  disableFixScrollbar: PropTypes.bool,
  sortedColumn: PropTypes.shape({}),
  columns: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  items: PropTypes.oneOfType([
    PropTypes.array,
  ]).isRequired,
  Renderer: PropTypes.shape({}).isRequired,
  onSort: PropTypes.func,
  onColumnResize: PropTypes.func,
  enableShimmer: PropTypes.bool,
  enableSegmentation: PropTypes.bool,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  toolbar: PropTypes.shape({}),
  stickyHeader: PropTypes.bool,
  stickyTop: PropTypes.number,
  pager: PropTypes.shape({
    pageNumber: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }),
  selectionMode: PropTypes.number,
  freezingColumnCount: PropTypes.number,
  freezingColumnRTLCount: PropTypes.number,
  onRenderRow: PropTypes.func,
  onRenderRowWithSegmentation: PropTypes.func,
  onRenderCheckbox: PropTypes.func,
  didUpdate: PropTypes.func,
  didMount: PropTypes.func,
  subColumnProps: PropTypes.shape({
    showSubColumns: PropTypes.bool,
    parentColumnPlacement: PropTypes.oneOf(_.values(ParentColumnPlacement)),
    expandIconName: PropTypes.string,
    collapseIconName: PropTypes.string,
  }),
  isSortedDescendingByDefault: PropTypes.bool,
  isGridScrollable: PropTypes.bool,
};

BaseCampaignDetailsList.defaultProps = {
  onSort: () => { },
  disableFixScrollbar: false,
  sortedColumn: {},
  onColumnResize: undefined,
  stickyHeader: false,
  stickyTop: 0,
  enableShimmer: false,
  enableSegmentation: false,
  placeholder: '',
  pager: null,
  toolbar: null,
  selectionMode: 0,
  freezingColumnCount: 0,
  freezingColumnRTLCount: 0,
  onRenderRow: undefined,
  onRenderRowWithSegmentation: _.noop,
  onRenderCheckbox: undefined,
  didUpdate: () => { },
  didMount: () => { },
  subColumnProps: {
    showSubColumns: false,
    parentColumnPlacement: ParentColumnPlacement.Center,
    expandIconName: 'Add',
    collapseIconName: 'Remove',
  },
  isSortedDescendingByDefault: true,
  isGridScrollable: false,
};

const Wrapped = _.compose(
  WrappedComponent => withDefaultStyles(WrappedComponent, gridStyle),
  withSegmentation,
  withSubColumns
)(BaseCampaignDetailsList);

export { Wrapped as BaseCampaignDetailsList };
